import Sortable from "@stimulus-components/sortable"

/**
 * 取扱い商品の並び替え・編集を管理するコントローラー
 * - ドラッグ&ドロップによる並び替え
 * - トグルスイッチによる取扱い状態の変更
 * - 変更の検知と保存
 */
export default class extends Sortable {
  connect() {
    super.connect()
    this.hasChanges = false
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  /**
   * カスタムイベントリスナーを設定
   */
  setupEventListeners() {
    this.boundSaveSortOrder = this.saveSortOrder.bind(this)
    this.boundHandleTabClick = this.handleTabClick.bind(this)
    this.boundHandleToggleChange = this.handleToggleChange.bind(this)

    document.addEventListener('save-sort-order', this.boundSaveSortOrder)
    document.addEventListener('handle-tab-click', this.boundHandleTabClick)
    this.element.addEventListener('change', this.boundHandleToggleChange)
  }

  /**
   * カスタムイベントリスナーを解除（メモリリーク防止）
   */
  removeEventListeners() {
    document.removeEventListener('save-sort-order', this.boundSaveSortOrder)
    document.removeEventListener('handle-tab-click', this.boundHandleTabClick)
    this.element.removeEventListener('change', this.boundHandleToggleChange)
  }

  /**
   * トグルスイッチの変更を検知して変更フラグを立てる
   * @param {Event} event - change イベント
   */
  handleToggleChange(event) {
    if (event.target.classList.contains('toggle')) {
      this.hasChanges = true
    }
  }

  /**
   * ドラッグ&ドロップによる並び替え完了時に変更フラグを立てる
   * @param {Event} event - SortableJSのupdateイベント
   */
  onUpdate(event) {
    super.onUpdate(event)
    this.hasChanges = true
  }

  /**
   * タブクリック時の処理
   * 変更がある場合は確認ダイアログを表示し、OKなら遷移
   * @param {CustomEvent} event - handle-tab-clickイベント
   */
  handleTabClick(event) {
    if (!this.hasChanges) {
      return this.navigateTo(event.detail?.href)
    }

    if (confirm(this.element.dataset.tabChangeMessage)) {
      this.navigateTo(event.detail?.href)
    }
  }

  /**
   * 指定されたURLに遷移
   * @param {string} href - 遷移先のURL
   */
  navigateTo(href) {
    if (href) {
      window.location.href = href
    }
  }

  /**
   * 並び順を保存（登録ボタン押下時）
   * 変更がない場合はエラー、ある場合は確認ダイアログ表示後に送信
   */
  saveSortOrder() {
    if (!this.hasChanges) {
      return alert(this.element.dataset.noChangesMessage)
    }

    if (!confirm(this.element.dataset.confirmMessage)) {
      return
    }

    this.submitSortOrder()
  }

  /**
   * 並び順データをサーバーに送信
   */
  submitSortOrder() {
    const availableItems = this.collectAvailableItems()
    const { itemType, branchId } = this.element.dataset

    fetch('/available_items/update_sort_order', {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({
        available_items: availableItems,
        item_type: itemType,
        branch_id: branchId
      })
    }).then(response => this.handleResponse(response))
  }

  /**
   * 現在の並び順とトグル状態を収集
   * @returns {Array} 取扱い商品データの配列
   */
  collectAvailableItems() {
    return this.sortable.toArray().map((id, index) => {
      const row = this.element.querySelector(`tr[data-id="${id}"]`)
      const toggle = row?.querySelector('.toggle')

      return {
        id,
        sort_order: index + 1,
        is_deleted: toggle ? !toggle.checked : null
      }
    })
  }

  /**
   * fetch用のHTTPヘッダーを生成
   * @returns {Object} HTTPヘッダー
   */
  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
    }
  }

  /**
   * サーバーからのレスポンスを処理
   * @param {Response} response - fetchのレスポンス
   * @returns {Response} レスポンスを返す（チェーン用）
   */
  handleResponse(response) {
    if (response.ok) {
      this.hasChanges = false
    }
    if (response.redirected) {
      window.location.href = response.url
    }
    return response
  }
}
